import { APP_BASE_HREF } from '@angular/common';
import { HttpBackend, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MsalInterceptor } from '@azure/msal-angular';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Evita que MsalInterceptor intente obtener tokens para el chat embebido (iframe/widget).
 * El mapa MSAL cubre toda la API; en /chat/* la app solo usa endpoints públicos y el intento de
 * acquireTokenSilent falla en iframe y dispara InteractionType.Redirect.
 */
@Injectable()
export class PublicChatMsalSkipInterceptor implements HttpInterceptor {
  private readonly msalInterceptor = inject(MsalInterceptor);
  private readonly httpBackend = inject(HttpBackend);
  private readonly router = inject(Router);
  private readonly baseHref = inject(APP_BASE_HREF, { optional: true }) ?? '/';

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.shouldBypassMsal(req)) {
      return this.httpBackend.handle(req);
    }
    return this.msalInterceptor.intercept(req, next);
  }

  /** Quita `<base href>` para comparar solo el segmento de aplicación (p. ej. `/chat/...`). */
  private stripApplicationBase(pathname: string): string {
    let base = this.baseHref;
    if (!base || base === '/') {
      return pathname.startsWith('/') ? pathname : '/' + pathname;
    }
    base = base.endsWith('/') ? base.slice(0, -1) : base;
    if (!pathname.startsWith(base)) {
      return pathname.startsWith('/') ? pathname : '/' + pathname;
    }
    let rest = pathname.slice(base.length);
    if (!rest.startsWith('/')) {
      rest = '/' + rest;
    }
    return rest || '/';
  }

  /** Ruta del shell público del widget (`/chat/:id/...`), no `/admin/chat/…`. */
  private isPublicChatShellRoute(): boolean {
    const normalize = (path: string): string => {
      if (path.length > 1 && path.endsWith('/')) {
        return path.slice(0, -1);
      }
      return path;
    };

    const matchesChatShell = (path: string): boolean => {
      const n = normalize(path);
      return n === '/chat' || n.startsWith('/chat/');
    };

    if (typeof window !== 'undefined') {
      const loc = normalize(this.stripApplicationBase(window.location.pathname || ''));
      if (matchesChatShell(loc)) {
        return true;
      }
    }

    const routePath = normalize(this.router.url.split('?')[0] || '');
    return matchesChatShell(routePath);
  }

  private shouldBypassMsal(req: HttpRequest<unknown>): boolean {
    const apiBase = environment.urlAPI.replace(/\/?$/, '/');
    const urlLower = req.url.toLowerCase();
    const apiLower = apiBase.toLowerCase();
    if (!urlLower.startsWith(apiLower)) {
      return false;
    }

    // Widget: todas las llamadas a la API van sin MSAL (endpoints públicos del chat).
    if (this.isPublicChatShellRoute()) {
      return true;
    }

    const rel = urlLower.slice(apiLower.length);
    if (rel.startsWith('chatbotmessage/')) {
      return true;
    }

    const publicEmbedPrefixes = [
      'chatbotchat/checkallowedorigin',
      'chatbotchat/getadditionalinfo/',
      'chatbotchat/getfilters/',
    ];
    return publicEmbedPrefixes.some((p) => rel.startsWith(p));
  }
}
